import React, { SyntheticEvent, useMemo, useState } from "react"

// ** Third Party Components
import { he } from "date-fns/locale"
import { AlertCircle } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { format, formatDistance, isToday, isYesterday } from "date-fns"

// ** Helpers
import { cn, formatTime, parseMessage } from "@pma/utils/helpers"

// ** Types
import { DictType } from "@pma/types"

// ** Theme Components
import { Badge } from "@components/common/badge"
import PmsLoader from "@components/common/loader"
import { Button } from "@components/common/button"
import { Separator } from "@components/common/separator"
import { Card, CardContent, CardDescription } from "@components/common/card"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"

// ** Store
import { useAuth } from "@stores/auth-store"
import { useDrawer } from "@stores/drawer-store"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"

// ** Navigation
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"

interface NotificationListProps {
  data: any
  onNotificationClick: () => void
  loadMore?: CallableFunction
  context: "all" | "unread" | "mentioned" | "assigned" | "logs"
  isLoading?: boolean
}

export const UpdateList: React.FC<NotificationListProps> = ({
  data,
  onNotificationClick,
  loadMore,
  context = "all",
  isLoading = false,
}) => {
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()

  const organizationUsers = authConfig?.organizationUsers
  const isHebrew = locale === "he"

  const updateLogsList = useMemo(() => {
    const temp: any = []
    data?.pages?.forEach((page: any) => {
      page?.items?.forEach((item: any) => {
        let title = "---"
        let note = false
        let valueKeys: any[] = []
        const breadcrumbsList: any = []

        const changes: DictType = item?.changes?.changes ? item.changes.changes[0] || {} : {}
        const prevValue = changes?.prev_value
        const newValue = changes?.new_value

        if (prevValue && Object.keys(prevValue).length > 0) {
          valueKeys = [...Object.keys(prevValue)]
        }
        if (newValue && Object.keys(newValue)?.length > 0) {
          valueKeys = [...Object.keys(newValue)]
        }

        if (valueKeys.includes("title")) {
          const condition = item?.changes?.changes[0]?.change_action?.en
          if (condition == "subtask added") {
            title = `${translate("Subtask was created")}: ${newValue?.title}`
          } else if (condition == "dependent task added") {
            title = `${translate("Task was edited")}: ${translate("Dependent task added")} - ${newValue?.title}`
          } else if (condition == '"depend on" task added') {
            title = `${translate("Task was edited")}: ${translate("depend on")} - ${newValue?.title}`
          } else if (condition == "dependent task deleted") {
            title = `${translate("Task was edited")}: ${translate("Dependent task deleted")} - ${prevValue?.title}`
          } else if (condition == '"depend on" task deleted') {
            title = `${translate("Task was edited")}: ${translate("depend on deleted ")} - ${prevValue?.title}`
          } else if (condition == "subtask deleted") {
            title = `${translate("Subtask was deleted")}: ${prevValue?.title}`
          } else {
            title = `${translate("Task title was changed from")} ${prevValue?.title} ${translate("to")} ${newValue?.title}`
          }
        } else if (valueKeys.includes("construction_name")) {
          if (item?.changes?.changes[0]?.change_action?.en == "edit project") {
            title = `${translate("Project Construction name was changed from")} ${prevValue.construction_name} ${translate("to")} ${newValue.construction_name}`
          }
        } else if (changes?.column_name === "responsible_id") {
          if (Object.keys(newValue).length > 0)
            title = `${translate("Task assigned to")} ${newValue?.name}`
          else if (Object.keys(prevValue).length > 0)
            title = `${translate("Remove assigned to")} ${prevValue?.name}`
        } else if (valueKeys.includes("note") || valueKeys.includes("Note")) {
          const condition = item?.changes?.changes[0]?.change_action?.en
          if (condition == "note was added") {
            title = `${translate("Note was created")}: ${parseMessage(newValue?.note, organizationUsers)}`
          } else if (condition == "note was deleted") {
            title = `${translate("Note was deleted")}:  ${parseMessage(prevValue?.note, organizationUsers)}`
          } else {
            title = `${translate("Note text was edited from")} ${parseMessage(prevValue?.note, organizationUsers)} ${translate("to")} ${parseMessage(newValue?.note, organizationUsers)}`
          }
          note = true
        } else if (valueKeys.includes("file")) {
          title = `${translate("New file was added")}.`
        } else if (valueKeys.includes("finish_to") || valueKeys.includes("start_at")) {
          const changesObj = item.changes?.changes ? item.changes?.changes[0] : null
          if (changesObj) {
            const changeAction = changesObj.change_action
            if (changeAction[locale] === "start time was changed") {
              const oldTime = changesObj.prev_value.start_at
                ? new Date(changesObj.prev_value.start_at)
                : null
              const newTime = changesObj.new_value?.start_at
                ? new Date(changesObj.new_value?.start_at)
                : null
              if (oldTime && newTime) {
                const fromDate = `${oldTime.getDate()}/${oldTime.getMonth() + 1}/${oldTime.getFullYear()}`
                const toDate = `${newTime.getDate()}/${newTime.getMonth() + 1}/${newTime.getFullYear()}`
                title = `${translate("Task start time was changed from")} ${fromDate} ${translate("to")} ${toDate}`
              }
            } else if (changeAction[locale] === "finish time was changed") {
              const oldTime = changesObj.prev_value.finish_to
                ? new Date(changesObj.prev_value.finish_to)
                : null
              const newTime = changesObj.new_value?.finish_to
                ? new Date(changesObj.new_value?.finish_to)
                : null
              if (oldTime && newTime) {
                const fromDate = `${oldTime.getDate()}/${oldTime.getMonth() + 1}/${oldTime.getFullYear()}`
                const toDate = `${newTime.getDate()}/${newTime.getMonth() + 1}/${newTime.getFullYear()}`
                title = `${translate("Task finish time was changed from")} ${fromDate} ${translate("to")} ${toDate}`
              }
            }
          }
        } else if (item.changes?.change_type?.en == "new task") {
          title = `${translate("New task created")}: ${item.changes?._task?.title}`
        } else if (changes?.column_name === "approval") {
          title = `${translate("Edit work diary")}: ${translate("Approval was changed")}.`
        } else if (valueKeys.includes("description")) {
          title = `${translate("Task description was changed")}: ${newValue?.description}`
        } else if (changes?.column_name === "status_id") {
          if (item.changes?.change_type?.en == "edit work diary") {
            title = `${translate("Work diary status was changed from")} ${prevValue?.en} ${translate("to")} ${newValue?.en}`
          } else {
            title = `${translate("Status was changed from")} ${prevValue?.name} ${translate("to")} ${newValue?.name}`
          }
        } else if (item.changes?.change_type?.en == "new project") {
          title = `${translate("The project was created")}`
        } else if (item.changes?.change_type?.en == "edit project") {
          title = `${translate("The project was edited")}: ${item._project?.title}`
        } else if (item.changes?.change_type?.en == "delete task") {
          title = `${translate("Deleted task")}: ${item?.deleted_object?.title}`
        } else if (item.changes?.change_type?.en == "new work diary") {
          title = `${translate("Work diary was created")}`
        } else if (item.changes?.change_type?.en == "delete work diary") {
          title = `${translate("Work diary was deleted")}: ${item?.deleted_object?.number}`
        } else if (item.changes?.change_type?.en == "edit work diary") {
          if (item?.changes?.changes[0]?.change_action?.en == "address was changed") {
            title = `${translate("Work diary address was changed from")} ${prevValue.address} ${translate("to")} ${newValue.address}`
          }
        } else if (valueKeys.includes("annotation")) {
          const condition = item?.changes?.changes[0]?.change_action?.en
          if (condition == "file annotation was deleted")
            title = `${translate("Annotation image file was deleted")}.`
          else title = `${translate("Annotation image file was modified")}.`
        } else if (item.changes?.change_type?.en == "new bill") {
          title = `${translate("New bill created")}`
        } else if (item.changes?.change_type?.en == "edit bill") {
          title = `${translate("Bill was edited")}`
        } else if (item.changes?.change_type?.en == "mark bill as deleted") {
          title = `${translate("Bill was deleted")}: ${item?.deleted_object?.number}`
        }

        // Breadcrumbs Order
        if (Object.keys(item?.breadcrumbs).length) {
          if (item?.breadcrumbs?.project) {
            const permission = {
              is_project_group: false,
              is_project: false,
              is_task: false,
              is_diary: false,
            }
            item?.breadcrumbs?.project?.project_groups.map((group: any) =>
              breadcrumbsList.push({
                ...permission,
                is_project_group: true,
                id: group.group_id,
                title: group.group_name,
              }),
            )
            breadcrumbsList.push({
              ...permission,
              is_project: true,
              id: item?.breadcrumbs?.project.project_id,
              title: item?.breadcrumbs?.project.project_name,
            })
          } else {
            breadcrumbsList.push({
              is_project_group: false,
              is_task: false,
              is_diary: false,
              is_project: true,
              id: item?.breadcrumbs?.project_id,
              title: item?.breadcrumbs?.project_name,
            })
          }

          if (item?.breadcrumbs?.task) {
            const permission = {
              is_project: false,
              is_task: true,
              is_diary: false,
              is_project_group: false,
            }
            if (Object.keys(item?.breadcrumbs?.task?.tasks_group).length)
              breadcrumbsList.push({
                ...permission,
                id: item?.breadcrumbs?.task.task_id,
                title: item?.breadcrumbs?.task.tasks_group?.tasks_group_name,
              })

            if (Object.keys(item?.breadcrumbs?.task?.parent_task).length)
              breadcrumbsList.push({
                ...permission,
                id: item?.breadcrumbs?.task?.parent_task?.parent_tasks_id,
                title: item?.breadcrumbs?.task?.parent_task?.parent_tasks_number,
              })

            breadcrumbsList.push({
              ...permission,
              id: item?.breadcrumbs?.task.task_id,
              title: item?.breadcrumbs?.task.task_number,
            })
          }

          if (item?.breadcrumbs?.diary) {
            if (Object.keys(item?.breadcrumbs?.diary).length) {
              breadcrumbsList.push({
                is_project: false,
                is_task: false,
                is_diary: true,
                is_project_group: false,
                id: item?.breadcrumbs?.diary.diary_id,
                title: item?.breadcrumbs?.diary.diary_number,
              })
            }
          }
        }

        temp.push({
          id: item.id,
          title: title,
          note: note,
          created_at: item?.created_at,
          _is_liked: item?._is_liked,
          _is_read: item?._is_readed,
          liked_by_id: item?.liked_by_id,
          user: item?._user,
          task_id: item?.task_id,
          breadcrumbs: breadcrumbsList,
          recepients: item?.recepients || [],
          _project: item?._project,
        })
      })
    })
    return temp
  }, [data])

  const groupedUpdatedLogs = useMemo(() => {
    const groups: Record<string, any[]> = {}

    updateLogsList.forEach((notification: any) => {
      const date = new Date(notification.created_at)
      let groupLabel = format(date, "PPP")

      if (isToday(date)) groupLabel = translate("Today")
      else if (isYesterday(date)) groupLabel = translate("Yesterday")

      if (!groups[groupLabel]) groups[groupLabel] = []
      groups[groupLabel].push(notification)
    })

    return groups
  }, [updateLogsList, translate])

  if (isLoading) {
    return (
      <div className='w-full' style={{ marginBlock: "10px" }}>
        <PmsLoader />
      </div>
    )
  }

  return (
    <div className='flex h-[80vh] flex-col gap-2 overflow-y-auto overflow-x-hidden'>
      {updateLogsList.length === 0 ? (
        <div className='flex h-[90vh] flex-col items-center justify-center rounded-md border border-slate-300 text-2xl'>
          {translate("No items found")}
          <AlertCircle size={40} className='mt-2' />
        </div>
      ) : (
        Object.entries(groupedUpdatedLogs).map(([group, updateLogs]) => (
          <div key={group} className='flex flex-col gap-2'>
            <p className='font-[inter] text-[10px] font-normal text-neutral-500'>{group}</p>
            {updateLogs.map((log) => (
              <UpdateLogCardInline
                key={log.id}
                log={log}
                context={context}
                onNotificationClick={onNotificationClick}
              />
            ))}
          </div>
        ))
      )}
      {loadMore && (
        <Button
          variant={"link"}
          loading={isLoading}
          className='m-2 w-full'
          onClick={() => loadMore && loadMore(context)}
        >
          {translate("Load More")}
        </Button>
      )}
    </div>
  )
}

// ---------- Inline Card (design from notification-card.tsx, without NotificationActions) ----------

interface UpdateLogCardInlineProps {
  log: any
  context?: "all" | "unread" | "mentioned" | "assigned" | "logs"
  onNotificationClick: () => void
}

const UpdateLogCardInline: React.FC<UpdateLogCardInlineProps> = ({
  log,
  context,
  onNotificationClick,
}) => {
  const { translate, locale } = useTranslator()
  const { setTabManager } = useAuth()
  const navigate = useNavigate()
  const drawer = useDrawer()

  const isHebrew = locale === "he"

  const [isAssignUserList, setIsAssignUserList] = useState<boolean>(false)

  const handleCardClick = () => {
    const taskId = log?.task_id
    const projectId = log?._project?.id || log?.breadcrumbs?.find((b: any) => b.is_project)?.id

    if (taskId) {
      context !== "logs" && drawer.onOpen()
      if (drawer.context === "updates") {
        drawer.setFromUpdates(true)
      } else {
        drawer.setFromUpdates(false)
      }
      drawer.setContext("notes")
      drawer.setTaskId(taskId)
      setTabManager({ group: "project-detail", main: "tasks", sub: "groups" })

      navigate(PROJECTS_PAGE_ROUTE + `/${projectId}?task=${taskId}&showBtn=`)
    } else if (projectId) {
      drawer.onClose()
      navigate(PROJECTS_PAGE_ROUTE + `/${projectId}`)
    }
  }

  const handleAssignList = (e: SyntheticEvent) => {
    e.stopPropagation()
    setIsAssignUserList(!isAssignUserList)
  }

  const getAssignUserList = (arr: any) => {
    if (!arr || arr.length === 0) return null

    if (arr.length >= 3) {
      return (
        <Popover>
          <div className='flex items-center'>
            {arr?.slice(0, 2).map((user: any, index: number) => (
              <Tooltip key={index}>
                <TooltipTrigger>
                  <Avatar className={cn("relative h-5 w-5", isHebrew ? "-mr-2" : "-ml-2")}>
                    <AvatarImage
                      src={user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`}
                    />
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent>
                  <h4> {user?.name}</h4>
                </TooltipContent>
              </Tooltip>
            ))}
            {arr?.length > 2 && (
              <PopoverTrigger onClick={handleAssignList}>
                <div
                  className={cn(
                    "relative h-5 w-5 rounded-full bg-blue-100",
                    isHebrew ? "-mr-2" : "-ml-2",
                  )}
                >
                  +{arr.length - 2}
                </div>
              </PopoverTrigger>
            )}
          </div>

          <PopoverContent className='max-h-[350px] max-w-[250px] overflow-y-auto'>
            <>
              {arr?.length > 0 && isAssignUserList && (
                <>
                  <div className='flex w-full flex-wrap gap-2'>
                    {arr?.map((user: any, index: number) => (
                      <Badge
                        className='relative rounded-full bg-slate-500/60 text-white'
                        variant={"outline"}
                        key={index}
                      >
                        {user.name}
                      </Badge>
                    ))}
                  </div>
                  <Separator className='my-2' />
                </>
              )}
              {arr?.map((user: DictType, index: number) => (
                <Tooltip key={index} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <div className={"flex flex-row gap-2"}>
                      <Avatar className={cn("h-7 w-7", isHebrew ? "-mr-2.5" : "-ml-2.5")}>
                        <AvatarImage
                          src={
                            user.user_picture
                              ? user.user_picture.url
                              : `${process.env.PUBLIC_URL}/usr.svg`
                          }
                          alt='Avatar'
                        />
                        <AvatarFallback className='uppercase'>{user?.name}</AvatarFallback>
                      </Avatar>
                      <span className=''>{user.name}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side='bottom' className='flex items-center gap-4'>
                    {user.name}
                  </TooltipContent>
                </Tooltip>
              ))}
            </>
          </PopoverContent>
        </Popover>
      )
    } else if (arr.length <= 2) {
      return arr?.map((user: DictType, index: number) => (
        <Tooltip key={index} delayDuration={0}>
          <TooltipTrigger asChild>
            <div className={""}>
              <Avatar className={cn("h-7 w-7", isHebrew ? "-mr-2.5" : "-ml-2.5")}>
                <AvatarImage
                  src={
                    user.user_picture ? user.user_picture.url : `${process.env.PUBLIC_URL}/usr.svg`
                  }
                  alt='Avatar'
                />
                <AvatarFallback className='uppercase'>
                  {user?.first_name && user?.first_name[0]}
                  {user?.last_name && user?.last_name[0]}
                </AvatarFallback>
              </Avatar>
              <span className='sr-only'>{user.name}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side='bottom' className='flex items-center gap-4'>
            {user.name}
          </TooltipContent>
        </Tooltip>
      ))
    }
  }

  return (
    <Card
      className={`relative flex h-max flex-col justify-center p-0 ${log?._is_read ? "shadow" : "bg-primary/10"}`}
    >
      <CardContent className='flex h-full overflow-y-hidden p-2' onClick={handleCardClick}>
        <div className='hidden md:flex'>
          <img
            src={log?.user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`}
            alt={log?.user?.name}
            aria-hidden='true'
            className={cn("h-8 w-8 rounded-full", isHebrew ? "ml-1" : "mr-1")}
          />
        </div>
        <CardDescription className='flex w-full flex-1 flex-col'>
          <div className='flex w-full flex-col'>
            <div className='flex flex-col gap-2'>
              <div className='flex flex-wrap items-center gap-1 text-xs'>
                <span className='font-semibold'>{log?.user?.name}</span>
                {log.note ? (
                  <span
                    className='max-w-[25ch] truncate text-ellipsis font-semibold text-primary'
                    dangerouslySetInnerHTML={{ __html: log.title }}
                  />
                ) : (
                  <Tooltip>
                    <TooltipTrigger>
                      <p className='max-w-[25ch] truncate text-ellipsis font-semibold text-primary'>
                        {log.title}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent className='bg-white text-black'>
                      <p>{log.title}</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>

              <div className={`flex items-end justify-between text-slate-500`}>
                <p>
                  <small>
                    {log?.created_at &&
                      formatDistance(log?.created_at, new Date(), {
                        locale: isHebrew ? he : undefined,
                        addSuffix: true,
                      })}
                  </small>{" "}
                  <small>
                    {" "}
                    @{" "}
                    {formatTime(log?.created_at, {
                      amPm: true,
                      locale: isHebrew ? he : undefined,
                    })}{" "}
                  </small>
                </p>
              </div>
            </div>
            <div className='flex cursor-pointer items-center justify-end truncate text-ellipsis pe-3'>
              {getAssignUserList(log?.recepients)}
            </div>
          </div>
        </CardDescription>
      </CardContent>
    </Card>
  )
}

export default UpdateList
